<template>
  <div class="slot-admin-config">
    <!-- Header -->
    <div class="config-header">
      <h2 class="config-title">{{ $t('admin.config.slots.title') }}</h2>
      <p class="config-subtitle">{{ $t('admin.config.slots.subtitle') }}</p>
    </div>

    <!-- Tab bar -->
    <div class="tab-bar" role="tablist">
      <button
        v-for="tab in tabs"
        :key="tab.id"
        class="tab-btn"
        :class="{ active: activeTab === tab.id }"
        role="tab"
        :aria-selected="activeTab === tab.id"
        @click="switchTab(tab.id)"
      >
        {{ tab.label }}
      </button>
    </div>

    <!-- Tab: Titles List -->
    <div v-if="activeTab === 'list'" class="tab-panel">
      <div v-if="listLoading" class="loading-state">{{ $t('admin.config.slots.loading_titles') }}</div>
      <div v-else-if="listError" class="error-banner">{{ listError }}</div>
      <template v-else>
        <div v-if="titles.length === 0" class="empty-state">
          {{ $t('admin.config.slots.no_titles') }}
        </div>
        <div v-else class="titles-grid">
          <div v-for="title in titles" :key="title.id" class="title-card">
            <div class="title-card-header">
              <h3 class="title-name">{{ title.name }}</h3>
              <span
                class="volatility-badge"
                :class="volatilityClass(title.volatility)"
              >
                {{ title.volatility || 'N/A' }}
              </span>
            </div>
            <p class="title-description">{{ title.description || $t('admin.config.slots.no_description') }}</p>
            <div class="title-meta">
              <span class="meta-item">{{ title.reels }}x{{ title.rows }}</span>
              <span class="meta-item">{{ formatGridType(title.grid_type) }}</span>
              <span v-if="title.payline_count" class="meta-item">
                {{ title.payline_count }} lines
              </span>
              <span v-if="title.target_rtp" class="meta-item rtp-badge">
                RTP {{ title.target_rtp }}%
              </span>
            </div>
            <div v-if="title.enabled_features?.length" class="title-features">
              <span
                v-for="feat in title.enabled_features"
                :key="feat"
                class="feature-tag"
              >
                {{ formatFeature(feat) }}
              </span>
            </div>
            <div v-if="title.error" class="error-banner small">{{ title.error }}</div>
            <div class="title-actions">
              <button class="btn btn-sm btn-primary" @click="editTitle(title.id)">
                {{ $t('admin.config.slots.btn_edit') }}
              </button>
              <button class="btn btn-sm btn-ghost" @click="cloneTitle(title.id, title.name)">
                {{ $t('admin.config.slots.btn_clone') }}
              </button>
              <button class="btn btn-sm btn-ghost" @click="previewTitle(title.id)">
                {{ $t('admin.config.slots.btn_preview') }}
              </button>
              <button class="btn btn-sm btn-danger" @click="deleteTitle(title.id, title.name)">
                {{ $t('admin.config.slots.btn_delete') }}
              </button>
            </div>
          </div>
        </div>
      </template>
    </div>

    <!-- Tab: Create / Edit -->
    <div v-if="activeTab === 'editor'" class="tab-panel">
      <div class="editor-header">
        <h3 class="editor-title">
          {{ editingId ? $t('admin.config.slots.editor_title_editing', { id: editingId }) : $t('admin.config.slots.editor_title_new') }}
        </h3>
        <button
          v-if="editingId"
          class="btn btn-sm btn-ghost"
          @click="resetEditor"
        >
          {{ $t('admin.config.slots.btn_create_new') }}
        </button>
      </div>

      <div v-if="editorError" class="error-banner">{{ editorError }}</div>
      <div v-if="editorSuccess" class="success-banner">{{ editorSuccess }}</div>

      <div class="editor-area">
        <label class="form-label editor-label">
          {{ $t('admin.config.slots.label_json_config') }}
          <textarea
            v-model="editorJson"
            class="json-editor"
            spellcheck="false"
            rows="30"
            :placeholder="$t('admin.config.slots.placeholder_json')"
          />
        </label>
      </div>

      <div class="editor-actions">
        <button
          class="btn btn-primary"
          :disabled="editorSaving"
          @click="saveEditor"
        >
          {{ editorSaving ? $t('admin.config.slots.btn_saving') : $t('admin.config.slots.btn_save') }}
        </button>
        <button
          class="btn btn-ghost"
          @click="validateEditor"
        >
          {{ $t('admin.config.slots.btn_validate') }}
        </button>
      </div>
    </div>

    <!-- Tab: Preview -->
    <div v-if="activeTab === 'preview'" class="tab-panel">
      <div v-if="!previewSlotId" class="empty-state">
        {{ $t('admin.config.slots.preview_empty') }}
      </div>
      <template v-else>
        <div class="preview-header">
          <h3 class="editor-title">Preview: {{ previewSlotId }}</h3>
          <div class="preview-controls">
            <label class="form-label inline-label">
              {{ $t('admin.config.slots.label_nonce') }}
              <input
                v-model.number="previewNonce"
                type="number"
                min="1"
                class="form-input form-input-sm"
              />
            </label>
            <button
              class="btn btn-primary btn-sm"
              :disabled="previewLoading"
              @click="runPreview"
            >
              {{ previewLoading ? $t('admin.config.slots.preview_spinning') : $t('admin.config.slots.preview_spin') }}
            </button>
          </div>
        </div>

        <div v-if="previewError" class="error-banner">{{ previewError }}</div>

        <div v-if="previewResult" class="preview-result">
          <!-- Grid display -->
          <div class="preview-grid">
            <div
              v-for="(col, reel) in previewResult.grid"
              :key="reel"
              class="preview-reel"
            >
              <div class="reel-header">R{{ reel }}</div>
              <div
                v-for="(sym, row) in col"
                :key="row"
                class="preview-symbol"
                :class="{ 'is-winner': isWinningPosition(reel, row) }"
              >
                {{ sym }}
              </div>
            </div>
          </div>

          <!-- Stop positions -->
          <div class="preview-info">
            <span class="info-label">{{ $t('admin.config.slots.preview_stop_positions') }}</span>
            <span class="info-value">{{ previewResult.stop_positions.join(', ') }}</span>
          </div>

          <!-- Wins -->
          <div v-if="previewResult.wins.length > 0" class="preview-wins">
            <h4 class="wins-title">{{ $t('admin.config.slots.preview_wins_header', { count: previewResult.wins.length }) }}</h4>
            <div
              v-for="(win, idx) in previewResult.wins"
              :key="idx"
              class="win-row"
            >
              <span class="win-symbol">{{ win.symbol }}</span>
              <span class="win-detail">
                {{ win.count || win.cluster_size || win.ways }}x match
              </span>
              <span class="win-multiplier">{{ win.multiplier }}x</span>
            </div>
            <div class="total-row">
              <strong>{{ $t('admin.config.slots.preview_total_multiplier', { value: previewResult.total_multiplier }) }}</strong>
            </div>
          </div>
          <div v-else class="no-wins">{{ $t('admin.config.slots.preview_no_wins') }}</div>
        </div>
      </template>
    </div>

    <!-- Clone modal -->
    <div v-if="showCloneModal" class="modal-overlay" @click.self="showCloneModal = false">
      <div class="modal-content">
        <h3 class="modal-title">{{ $t('admin.config.slots.clone_modal_title') }}</h3>
        <p class="modal-subtitle">{{ $t('admin.config.slots.clone_modal_subtitle', { id: cloneSourceId }) }}</p>
        <div class="modal-form">
          <label class="form-label">
            {{ $t('admin.config.slots.clone_label_id') }}
            <input
              v-model="cloneNewId"
              type="text"
              class="form-input"
              placeholder="e.g. egyptian_gold_v2"
              pattern="[a-z0-9_]+"
            />
          </label>
          <label class="form-label">
            {{ $t('admin.config.slots.clone_label_name') }}
            <input
              v-model="cloneNewName"
              type="text"
              class="form-input"
              placeholder="e.g. Egyptian Gold V2"
            />
          </label>
        </div>
        <div v-if="cloneError" class="error-banner small">{{ cloneError }}</div>
        <div class="modal-actions">
          <button
            class="btn btn-primary"
            :disabled="cloneSaving || !cloneNewId || !cloneNewName"
            @click="executeClone"
          >
            {{ cloneSaving ? $t('admin.config.slots.btn_saving') : $t('admin.config.slots.btn_clone') }}
          </button>
          <button class="btn btn-ghost" @click="showCloneModal = false">{{ $t('common.cancel') }}</button>
        </div>
      </div>
    </div>

    <!-- Delete confirmation modal -->
    <div v-if="showDeleteModal" class="modal-overlay" @click.self="showDeleteModal = false">
      <div class="modal-content">
        <h3 class="modal-title">{{ $t('admin.config.slots.delete_modal_title') }}</h3>
        <p class="modal-subtitle">{{ $t('admin.config.slots.delete_modal_subtitle', { name: deleteTargetName }) }}</p>
        <div class="modal-actions">
          <button
            class="btn btn-danger"
            :disabled="deleteSaving"
            @click="executeDelete"
          >
            {{ deleteSaving ? $t('admin.config.slots.btn_saving') : $t('admin.config.slots.btn_delete') }}
          </button>
          <button class="btn btn-ghost" @click="showDeleteModal = false">{{ $t('common.cancel') }}</button>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import { ref, onMounted } from 'vue'
import { useI18n } from 'vue-i18n'
import apiClient from '@/api/client'

// --------------- Types ---------------

interface SlotTitleSummary {
  id: string
  name: string
  description: string
  grid_type: string
  reels: number
  rows: number
  payline_count: number | null
  volatility: string
  theme: string
  target_rtp: number | null
  min_bet: number | null
  max_bet: number | null
  enabled_features: string[]
  error?: string
}

interface PreviewResult {
  slot_id: string
  grid: string[][]
  stop_positions: number[]
  wins: Array<{
    symbol: string
    count?: number
    cluster_size?: number
    ways?: number
    multiplier: number
    positions?: Array<{ reel: number; row: number }>
  }>
  total_multiplier: number
  seed_info: {
    server_seed: string
    client_seed: string
    nonce: number
  }
}

// --------------- Tabs ---------------

const { t } = useI18n()

type TabId = 'list' | 'editor' | 'preview'

const tabs: Array<{ id: TabId; label: string }> = [
  { id: 'list', label: t('admin.config.slots.tab_list') },
  { id: 'editor', label: t('admin.config.slots.tab_editor') },
  { id: 'preview', label: t('admin.config.slots.tab_preview') },
]
const activeTab = ref<TabId>('list')

function switchTab(tabId: TabId) {
  activeTab.value = tabId
}

// --------------- List state ---------------

const titles = ref<SlotTitleSummary[]>([])
const listLoading = ref(false)
const listError = ref('')

async function loadTitles() {
  listLoading.value = true
  listError.value = ''
  try {
    const res = await apiClient.get<{ titles: SlotTitleSummary[] }>(
      '/admin/api/games/slots/titles',
    )
    titles.value = res.data.titles
  } catch {
    listError.value = t('admin.config.slots.error_load')
  } finally {
    listLoading.value = false
  }
}

// --------------- Editor state ---------------

const editingId = ref('')
const editorJson = ref('')
const editorError = ref('')
const editorSuccess = ref('')
const editorSaving = ref(false)

function resetEditor() {
  editingId.value = ''
  editorJson.value = ''
  editorError.value = ''
  editorSuccess.value = ''
}

async function editTitle(slotId: string) {
  editorError.value = ''
  editorSuccess.value = ''
  try {
    const res = await apiClient.get<{ config: Record<string, unknown> }>(
      `/admin/api/games/slots/titles/${slotId}`,
    )
    editingId.value = slotId
    editorJson.value = JSON.stringify(res.data.config, null, 2)
    activeTab.value = 'editor'
  } catch {
    editorError.value = t('admin.config.slots.error_load_title')
    activeTab.value = 'editor'
  }
}

function validateEditor() {
  editorError.value = ''
  editorSuccess.value = ''
  try {
    JSON.parse(editorJson.value)
    editorSuccess.value = t('admin.config.slots.json_valid')
    setTimeout(() => { editorSuccess.value = '' }, 3000)
  } catch (e: unknown) {
    const err = e as { message?: string }
    editorError.value = t('admin.config.slots.error_json_invalid', { message: err?.message ?? '' })
  }
}

async function saveEditor() {
  editorError.value = ''
  editorSuccess.value = ''
  editorSaving.value = true

  let config: Record<string, unknown>
  try {
    config = JSON.parse(editorJson.value) as Record<string, unknown>
  } catch (e: unknown) {
    const err = e as { message?: string }
    editorError.value = t('admin.config.slots.error_json_invalid', { message: err?.message ?? '' })
    editorSaving.value = false
    return
  }

  try {
    if (editingId.value) {
      // Update existing
      await apiClient.put(
        `/admin/api/games/slots/titles/${editingId.value}`,
        { config },
      )
      editorSuccess.value = t('admin.config.slots.save_success_updated', { id: editingId.value })
    } else {
      // Create new
      if (!config.id) {
        editorError.value = t('admin.config.slots.error_must_have_id')
        editorSaving.value = false
        return
      }
      const res = await apiClient.post<{ config: Record<string, unknown> }>(
        '/admin/api/games/slots/titles',
        { config },
      )
      editingId.value = (res.data.config.id as string) || ''
      editorSuccess.value = t('admin.config.slots.save_success_created', { id: editingId.value })
    }
    setTimeout(() => { editorSuccess.value = '' }, 3000)
    void loadTitles()
  } catch (err: unknown) {
    const e = err as { response?: { data?: { message?: string; errors?: string[] } } }
    const errors = e?.response?.data?.errors
    editorError.value = errors?.join('; ') ?? e?.response?.data?.message ?? t('admin.config.slots.error_save')
  } finally {
    editorSaving.value = false
  }
}

// --------------- Clone state ---------------

const showCloneModal = ref(false)
const cloneSourceId = ref('')
const cloneNewId = ref('')
const cloneNewName = ref('')
const cloneError = ref('')
const cloneSaving = ref(false)

function cloneTitle(sourceId: string, sourceName: string) {
  cloneSourceId.value = sourceId
  cloneNewId.value = sourceId + '_copy'
  cloneNewName.value = sourceName + ' (Copy)'
  cloneError.value = ''
  showCloneModal.value = true
}

async function executeClone() {
  cloneError.value = ''
  cloneSaving.value = true
  try {
    await apiClient.post(`/admin/api/games/slots/titles/${cloneSourceId.value}/clone`, {
      new_id: cloneNewId.value,
      new_name: cloneNewName.value,
    })
    showCloneModal.value = false
    void loadTitles()
  } catch (err: unknown) {
    const e = err as { response?: { data?: { message?: string } } }
    cloneError.value = e?.response?.data?.message ?? t('admin.config.slots.error_clone')
  } finally {
    cloneSaving.value = false
  }
}

// --------------- Delete state ---------------

const showDeleteModal = ref(false)
const deleteTargetId = ref('')
const deleteTargetName = ref('')
const deleteSaving = ref(false)

function deleteTitle(slotId: string, name: string) {
  deleteTargetId.value = slotId
  deleteTargetName.value = name
  showDeleteModal.value = true
}

async function executeDelete() {
  deleteSaving.value = true
  try {
    await apiClient.delete(`/admin/api/games/slots/titles/${deleteTargetId.value}`)
    showDeleteModal.value = false
    void loadTitles()
  } catch {
    // Failed silently (modal stays open)
  } finally {
    deleteSaving.value = false
  }
}

// --------------- Preview state ---------------

const previewSlotId = ref('')
const previewNonce = ref(1)
const previewLoading = ref(false)
const previewError = ref('')
const previewResult = ref<PreviewResult | null>(null)

function previewTitle(slotId: string) {
  previewSlotId.value = slotId
  previewNonce.value = 1
  previewResult.value = null
  previewError.value = ''
  activeTab.value = 'preview'
  void runPreview()
}

async function runPreview() {
  previewLoading.value = true
  previewError.value = ''
  try {
    const res = await apiClient.get<PreviewResult>(
      `/admin/api/games/slots/titles/${previewSlotId.value}/preview`,
      { params: { nonce: previewNonce.value } },
    )
    previewResult.value = res.data
    previewNonce.value++
  } catch (err: unknown) {
    const e = err as { response?: { data?: { message?: string } } }
    previewError.value = e?.response?.data?.message ?? t('admin.config.slots.error_preview')
  } finally {
    previewLoading.value = false
  }
}

function isWinningPosition(reel: number, row: number): boolean {
  if (!previewResult.value) return false
  for (const win of previewResult.value.wins) {
    if (win.positions) {
      for (const pos of win.positions) {
        if (pos.reel === reel && pos.row === row) return true
      }
    }
  }
  return false
}

// --------------- Helpers ---------------

function formatGridType(type: string): string {
  const map: Record<string, string> = {
    paylines: 'Paylines',
    ways: 'Ways-to-Win',
    cluster: 'Cluster Pays',
  }
  return map[type] ?? type
}

function formatFeature(feat: string): string {
  return feat.replace(/_/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase())
}

function volatilityClass(vol: string): string {
  if (!vol) return ''
  const lower = vol.toLowerCase()
  if (lower.includes('high')) return 'vol-high'
  if (lower.includes('medium')) return 'vol-medium'
  if (lower.includes('low')) return 'vol-low'
  return ''
}

// --------------- Mount ---------------

onMounted(() => {
  void loadTitles()
})
</script>

<style scoped>
.slot-admin-config {
  max-width: 1040px;
}

/* Header */
.config-header {
  margin-bottom: 20px;
}
.config-title {
  font-size: 1.5rem;
  font-weight: 700;
  color: var(--text-primary, #fff);
  margin: 0 0 4px;
}
.config-subtitle {
  font-size: 0.875rem;
  color: var(--text-secondary, #b1bad3);
  margin: 0;
}

/* Tabs */
.tab-bar {
  display: flex;
  gap: 4px;
  border-bottom: 1px solid var(--border, #2d4a5a);
  margin-bottom: 24px;
}
.tab-btn {
  padding: 10px 20px;
  background: none;
  border: none;
  border-bottom: 2px solid transparent;
  color: var(--text-secondary, #b1bad3);
  font-size: 0.9rem;
  cursor: pointer;
  margin-bottom: -1px;
  transition: color 0.15s, border-color 0.15s;
}
.tab-btn.active {
  color: var(--accent, #1a9e6e);
  border-bottom-color: var(--accent, #1a9e6e);
}
.tab-btn:hover:not(.active) {
  color: var(--text-primary, #fff);
}

/* Tab panels */
.tab-panel {
  animation: fadeIn 0.2s ease;
}
@keyframes fadeIn {
  from { opacity: 0; transform: translateY(8px); }
  to { opacity: 1; transform: translateY(0); }
}

/* States */
.loading-state {
  padding: 40px;
  text-align: center;
  color: var(--text-secondary, #b1bad3);
}
.empty-state {
  padding: 40px;
  text-align: center;
  color: var(--text-secondary, #b1bad3);
  background: var(--bg-secondary, #213743);
  border: 1px solid var(--border, #2d4a5a);
  border-radius: 10px;
}
.error-banner {
  padding: 12px 16px;
  background: rgba(239, 68, 68, 0.12);
  border: 1px solid rgba(239, 68, 68, 0.4);
  border-radius: 8px;
  color: var(--color-danger-faint, #fca5a5);
  margin-bottom: 16px;
}
.error-banner.small {
  padding: 8px 12px;
  font-size: 0.8rem;
  margin-bottom: 8px;
}
.success-banner {
  padding: 12px 16px;
  background: rgba(26, 158, 110, 0.12);
  border: 1px solid rgba(26, 158, 110, 0.4);
  border-radius: 8px;
  color: var(--color-success-faint, #6ee7b7);
  margin-bottom: 16px;
}

/* Titles grid */
.titles-grid {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(320px, 1fr));
  gap: 16px;
}
.title-card {
  background: var(--bg-secondary, #213743);
  border: 1px solid var(--border, #2d4a5a);
  border-radius: 10px;
  padding: 16px;
  display: flex;
  flex-direction: column;
  gap: 8px;
}
.title-card-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
}
.title-name {
  font-size: 1rem;
  font-weight: 600;
  color: var(--text-primary, #fff);
  margin: 0;
}
.title-description {
  font-size: 0.8rem;
  color: var(--text-secondary, #b1bad3);
  margin: 0;
  line-height: 1.4;
}
.title-meta {
  display: flex;
  flex-wrap: wrap;
  gap: 6px;
}
.meta-item {
  font-size: 0.75rem;
  padding: 2px 8px;
  border-radius: 4px;
  background: rgba(107, 114, 128, 0.15);
  color: var(--text-secondary, #b1bad3);
}
.rtp-badge {
  background: rgba(26, 158, 110, 0.15);
  color: var(--color-success-faint, #6ee7b7);
}
.volatility-badge {
  font-size: 0.7rem;
  font-weight: 600;
  padding: 2px 8px;
  border-radius: 12px;
  text-transform: capitalize;
}
.vol-high { background: var(--color-danger-bg, rgba(220, 38, 38, 0.15)); color: var(--color-danger-faint, #fca5a5); border: 1px solid var(--color-danger-border, rgba(220, 38, 38, 0.4)); }
.vol-medium { background: var(--color-warning-bg, rgba(217, 119, 6, 0.15)); color: var(--color-warning-light, #fbbf24); border: 1px solid var(--color-warning-border, rgba(217, 119, 6, 0.4)); }
.vol-low { background: var(--color-success-bg, rgba(26, 158, 110, 0.15)); color: var(--color-success-faint, #6ee7b7); border: 1px solid var(--color-success-border, rgba(26, 158, 110, 0.4)); }
.title-features {
  display: flex;
  flex-wrap: wrap;
  gap: 4px;
}
.feature-tag {
  font-size: 0.7rem;
  padding: 2px 6px;
  border-radius: 4px;
  background: rgba(59, 130, 246, 0.15);
  color: var(--color-info-faint, #93c5fd);
}
.title-actions {
  display: flex;
  gap: 6px;
  margin-top: 4px;
  padding-top: 8px;
  border-top: 1px solid var(--border, #2d4a5a);
}

/* Editor */
.editor-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 16px;
}
.editor-title {
  font-size: 1.1rem;
  font-weight: 600;
  color: var(--text-primary, #fff);
  margin: 0;
}
.editor-area {
  margin-bottom: 16px;
}
.editor-label {
  display: flex;
  flex-direction: column;
  gap: 6px;
  font-size: 0.875rem;
  color: var(--text-secondary, #b1bad3);
}
.json-editor {
  width: 100%;
  padding: 12px;
  background: var(--bg-primary, #0f1923);
  border: 1px solid var(--border, #2d4a5a);
  border-radius: 8px;
  color: var(--text-primary, #fff);
  font-family: 'JetBrains Mono', 'Fira Code', monospace;
  font-size: 0.8rem;
  line-height: 1.5;
  resize: vertical;
  tab-size: 2;
}
.json-editor:focus {
  outline: none;
  border-color: var(--accent, #1a9e6e);
}
.editor-actions {
  display: flex;
  gap: 10px;
}

/* Preview */
.preview-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 16px;
  flex-wrap: wrap;
  gap: 12px;
}
.preview-controls {
  display: flex;
  align-items: flex-end;
  gap: 10px;
}
.inline-label {
  display: flex;
  flex-direction: column;
  gap: 4px;
  font-size: 0.8rem;
  color: var(--text-secondary, #b1bad3);
}
.form-input-sm {
  width: 80px;
  padding: 6px 8px;
  font-size: 0.8rem;
}
.preview-result {
  background: var(--bg-secondary, #213743);
  border: 1px solid var(--border, #2d4a5a);
  border-radius: 10px;
  padding: 20px;
}
.preview-grid {
  display: flex;
  gap: 8px;
  justify-content: center;
  margin-bottom: 16px;
}
.preview-reel {
  display: flex;
  flex-direction: column;
  gap: 4px;
  align-items: center;
}
.reel-header {
  font-size: 0.7rem;
  color: var(--text-secondary, #b1bad3);
  font-weight: 600;
}
.preview-symbol {
  width: 72px;
  height: 48px;
  display: flex;
  align-items: center;
  justify-content: center;
  background: var(--bg-primary, #0f1923);
  border: 1px solid var(--border, #2d4a5a);
  border-radius: 6px;
  color: var(--text-primary, #fff);
  font-size: 0.75rem;
  font-weight: 500;
}
.preview-symbol.is-winner {
  border-color: var(--accent, #1a9e6e);
  background: rgba(26, 158, 110, 0.15);
  color: var(--color-success-faint, #6ee7b7);
}
.preview-info {
  display: flex;
  gap: 8px;
  font-size: 0.8rem;
  color: var(--text-secondary, #b1bad3);
  margin-bottom: 12px;
}
.info-label {
  font-weight: 600;
}
.preview-wins {
  padding-top: 12px;
  border-top: 1px solid var(--border, #2d4a5a);
}
.wins-title {
  font-size: 0.9rem;
  font-weight: 600;
  color: var(--text-primary, #fff);
  margin: 0 0 8px;
}
.win-row {
  display: flex;
  gap: 12px;
  padding: 6px 0;
  border-bottom: 1px solid rgba(45, 74, 90, 0.5);
  font-size: 0.85rem;
}
.win-symbol {
  font-weight: 600;
  color: var(--text-primary, #fff);
  min-width: 80px;
}
.win-detail {
  color: var(--text-secondary, #b1bad3);
  flex: 1;
}
.win-multiplier {
  color: var(--color-success-faint, #6ee7b7);
  font-weight: 600;
}
.total-row {
  padding: 10px 0 0;
  font-size: 0.9rem;
  color: var(--color-success-faint, #6ee7b7);
}
.no-wins {
  color: var(--text-secondary, #b1bad3);
  font-size: 0.85rem;
  text-align: center;
  padding: 12px;
}

/* Modals */
.modal-overlay {
  position: fixed;
  inset: 0;
  background: rgba(0, 0, 0, 0.6);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1000;
}
.modal-content {
  background: var(--bg-secondary, #213743);
  border: 1px solid var(--border, #2d4a5a);
  border-radius: 12px;
  padding: 24px;
  min-width: 360px;
  max-width: 480px;
}
.modal-title {
  font-size: 1.1rem;
  font-weight: 700;
  color: var(--text-primary, #fff);
  margin: 0 0 4px;
}
.modal-subtitle {
  font-size: 0.875rem;
  color: var(--text-secondary, #b1bad3);
  margin: 0 0 16px;
}
.modal-form {
  display: flex;
  flex-direction: column;
  gap: 12px;
  margin-bottom: 16px;
}
.modal-actions {
  display: flex;
  gap: 10px;
}

/* Forms */
.form-label {
  display: flex;
  flex-direction: column;
  gap: 6px;
  font-size: 0.875rem;
  color: var(--text-secondary, #b1bad3);
}
.form-input {
  padding: 10px 12px;
  background: var(--bg-primary, #0f1923);
  border: 1px solid var(--border, #2d4a5a);
  border-radius: 8px;
  color: var(--text-primary, #fff);
  font-size: 0.9rem;
  transition: border-color 0.15s;
}
.form-input:focus {
  outline: none;
  border-color: var(--accent, #1a9e6e);
}

/* Buttons */
.btn {
  padding: 10px 20px;
  border-radius: 8px;
  border: none;
  font-size: 0.875rem;
  font-weight: 600;
  cursor: pointer;
  transition: opacity 0.15s, background 0.15s;
}
.btn:disabled {
  opacity: 0.5;
  cursor: not-allowed;
}
.btn-primary {
  background: var(--accent, #1a9e6e);
  color: var(--color-on-dark, #fff);
}
.btn-primary:hover:not(:disabled) {
  background: var(--accent-primary-hover, #1abc7e);
}
.btn-ghost {
  background: transparent;
  color: var(--text-secondary, #b1bad3);
  border: 1px solid var(--border, #2d4a5a);
}
.btn-ghost:hover:not(:disabled) {
  color: var(--text-primary, #fff);
  border-color: var(--text-secondary, #b1bad3);
}
.btn-danger {
  background: rgba(220, 38, 38, 0.15);
  color: var(--color-danger-faint, #fca5a5);
  border: 1px solid rgba(220, 38, 38, 0.4);
}
.btn-danger:hover:not(:disabled) {
  background: rgba(220, 38, 38, 0.3);
}
.btn-sm {
  padding: 6px 14px;
  font-size: 0.8rem;
}

/* Number inputs: remove arrows */
input[type='number']::-webkit-inner-spin-button,
input[type='number']::-webkit-outer-spin-button {
  -webkit-appearance: none;
}
input[type='number'] {
  -moz-appearance: textfield;
  appearance: textfield;
}
</style>
